import { ChangeEvent, useState } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDAlert from "components/MDAlert";
import MDTypography from "components/MDTypography";
import MapsLeaflet from "components/MapsLeaflet";

import FormField from "./EventoFormField";
import formCadastroEvento from "../schemas/forms";

import { cepApplyMask, numbersOnly } from "helpers/utils";
import { getCoordCity } from "helpers/getCoordCity";

import { getCep } from "services/uteis";

const { formField } = formCadastroEvento;
const {
  cep,
  logradouro,
  numero,
  bairro,
  cidade,
  uf,
  complemento,
  nomeDoLocal,
  latitude,
  longitude,
} = formField;

interface FieldDetailsProps {
  mapsExibir: boolean;
  values: {
    [x: string]: string;
  };
  handleBlur: {
    (e: React.FocusEvent<any>): void;
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void;
  };
  handleChange: {
    (e: ChangeEvent<any>): void;
    <T = string | ChangeEvent<any>>(
      field: T
    ): T extends ChangeEvent<any>
      ? void
      : (e: string | ChangeEvent<any>) => void;
  };
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handlerSetCoords: (
    coords: CoordsProps,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => void;
  handlerExibirMaps: (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => void;
}

interface CoordsProps {
  lat: number;
  lng: number;
}

export default function FieldMaps({
  values,
  mapsExibir,
  handleBlur,
  handleChange,
  setFieldValue,
  handlerSetCoords,
  handlerExibirMaps,
}: FieldDetailsProps) {
  const [isloadingCep, setIsloadingCep] = useState<boolean>(false);
  const [isMessageCep, setIsMessageCep] = useState<boolean>(false);

  async function handlerGetCep(
    cep: string,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) {
    setIsloadingCep(true);
    try {
      const valueCep = numbersOnly(cep).length >= 8;

      if (valueCep) {
        setIsMessageCep(false);
        // Realiza a chamada para obter os dados do CEP
        const resp = await getCep(cep);
        // Verifica se houve erro na consulta do CEP
        if (resp.erro) {
          // handlerMessage({ severity: "error", description: "Cep não localizado" });
          setIsMessageCep(true);
          setIsloadingCep(false);
          setFieldValue("logradouro", "");
          setFieldValue("cidade", "");
          setFieldValue("uf", "");
          setFieldValue("bairro", "");
          setFieldValue("latitude", "");
          setFieldValue("longitude", "");
          return;
        }
        // Realiza a chamada para obter as coordenadas da cidade
        const coords = await getCoordCity(
          resp.localidade || "",
          resp.uf || ""
        ).finally(() => setIsloadingCep(false));

        // Define os valores dos campos usando os dados recebidos
        setFieldValue("logradouro", resp.logradouro);
        setFieldValue("cidade", resp.localidade);
        setFieldValue("uf", resp.uf);
        setFieldValue("bairro", resp.bairro);

        // Verifica se coords é um array ou um objeto e define os valores dos campos de latitude e longitude
        if (Array.isArray(coords)) {
          setFieldValue("latitude", coords[0]);
          setFieldValue("longitude", coords[1]);
        } else {
          setIsMessageCep(true);
          setFieldValue("latitude", "");
          setFieldValue("longitude", "");
        }
      } else {
        setIsloadingCep(false);
        setIsMessageCep(true);
      }
    } catch (error) {
      setIsloadingCep(false);
      setIsMessageCep(true);
      setFieldValue("latitude", "");
      setFieldValue("longitude", "");
      console.error("Erro ao obter o CEP ou coordenadas:", error);
    }
  }

  return (
    <Card id="MAPS" sx={{ overflow: "visible", margin: "10px 0" }}>
      <MDBox p={3} lineHeight={1}>
        <MDBox mb={1}>
          <MDTypography variant="h5">Definição do local do evento</MDTypography>
        </MDBox>
        <MDTypography variant="button" color="text">
          Informe o endereço e as coordenadas usadas para exibir o local do
          evento.
        </MDTypography>
      </MDBox>

      <MDBox pb={3} px={3}>
        {isMessageCep && (
          <MDAlert color="primary" dismissible>
            Cep não localizado
          </MDAlert>
        )}

        <Grid container spacing={3} sx={{ my: 1 }}>
          <Grid item xs={12} sm={4} sx={{ position: "relative" }}>
            <FormField
              type={cep.type}
              label={cep.label}
              name={cep.name}
              required
              variant="outlined"
              value={
                (values.cep || "").length === 8
                  ? cepApplyMask(values.cep)
                  : numbersOnly(values.cep)
              }
              onChange={handleChange}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                handleBlur(event);
                handlerGetCep(values.cep, setFieldValue);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 10 }}
            />
            <CircularProgress
              sx={{
                display: isloadingCep ? "flex" : "none",
                position: "absolute",
                right: 10,
                top: 40,
              }}
              size={20}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormField
              type={logradouro.type}
              label={logradouro.label}
              name={logradouro.name}
              required
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.logradouro}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <FormField
              type={numero.type}
              label={numero.label}
              name={numero.name}
              required
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.numero}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ my: 1 }}>
          <Grid item xs={12} sm={2}>
            <FormField
              type={complemento.type}
              label={complemento.label}
              name={complemento.name}
              //   error={Boolean(touched.complemento && errors.complemento)}
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.complemento}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormField
              type={bairro.type}
              label={bairro.label}
              name={bairro.name}
              //   error={Boolean(touched.bairro && errors.bairro)}
              required
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.bairro}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormField
              type={cidade.type}
              label={cidade.label}
              name={cidade.name}
              //   error={Boolean(touched.cidade && errors.cidade)}
              required
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.cidade}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <FormField
              type={uf.type}
              label={uf.label}
              name={uf.name}
              //   error={Boolean(touched.uf && errors.uf)}
              required
              variant="outlined"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setFieldValue(uf.name, event.target.value.toUpperCase())
              }
              onBlur={handleBlur}
              value={values.uf}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ my: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormField
              type={nomeDoLocal.type}
              label={nomeDoLocal.label}
              name={nomeDoLocal.name}
              //   error={Boolean(touched.nomeDoLocal && errors.nomeDoLocal)}
              required
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.nomeDoLocal}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormField
              type={latitude.type}
              label={latitude.label}
              name={latitude.name}
              //   error={Boolean(touched.latitude && errors.latitude)}
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.latitude}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormField
              type={longitude.type}
              label={longitude.label}
              name={longitude.name}
              //   error={Boolean(touched.longitude && errors.longitude)}
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.longitude}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </MDBox>

      <MDBox pb={3} px={3}>
        <MDTypography
          variant="caption"
          fontWeight="bold"
          color="text"
          textTransform="uppercase"
        >
          Exibição mapa no site
        </MDTypography>
        <Grid container spacing={3} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={6}>
            <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
              <MDBox mt={0.5}>
                <Switch
                  checked={mapsExibir}
                  onChange={() => handlerExibirMaps(setFieldValue)}
                />
              </MDBox>
              <MDBox width="80%" ml={0.5}>
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Exibir o mapa no site?
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      {!isNaN(Number(values.latitude)) &&
        !isNaN(Number(values.longitude)) &&
        values.latitude &&
        values.longitude && (
          <MDBox pb={3} px={3}>
            <MDBox width="100%" height="100%">
              <MapsLeaflet
                handlerSetCoords={(coords: CoordsProps) =>
                  handlerSetCoords(coords, setFieldValue)
                }
                lat={Number(values.latitude)}
                lng={Number(values.longitude)}
              />
            </MDBox>
          </MDBox>
        )}
    </Card>
  );
}
